import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Row, Col, Card, Button, Spinner, Container } from 'react-bootstrap';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { PutJson, PostJson } from '@awp/helpers/fetchReact';
import { REST_API, FORM_MODE } from '@awp/constants';
import {
  FormInput,
  FormTextArea,
  FormSelect,
  FormSpatialInput,
  FormCheckbox,
  FormListWithAction,
} from '@awp/components/Common/AwpFormInputs';
import { isValueEmpty, isIntStr } from '@awp/helpers/utilsReact';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import { getAvalancheGeographicAreas, selectGeographicAreaLinesDd } from '@awp/stores/avalancheSlice';
import { GetJson } from '@awp/helpers/fetchReact';
import { Permissions } from '@awp/constants';
import Authorize from '@awp/components/Authorize';
import AwpCloseCancelButtons from '../../components/AwpCloseCancelButton';
import RoadClosureLocationsService from '@components/RoadClosureLocationsAdmin/RoadClosureLocationsService';
import isEqual from 'lodash/isEqual';

const RoadClosureLocationsEdit = ({ setMode, mode, area, setArea, data, setData, areaPoints, setAreaPoints }) => {
  const [loading, setLoading] = useState(false);
  const [roadClosureLocationData, setRoadClosureLocationData] = useState(mode === FORM_MODE.CREATE ? {} : { ...data });
  const [initialDto, setInitialDto] = useState({});
  const [entityErrors, setEntityErrors] = useState(null);
  const [areaId, setAreaId] = useState(null);
  const navigate = useNavigate();
  const areaLinesRaw = useSelector((state) => selectGeographicAreaLinesDd(state));
  const [areaLines, setAreaLines] = useState(
    areaLinesRaw.filter((x) => x.geographicPointType === 'ROAD CLOSURE POINT'),
  );

  const crudService = new RoadClosureLocationsService(mode);
  const { readOnly, title, successMessage } = crudService.crud;

  const programAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM'));
  const avalancheAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE AREA'));
  const [lineList, setLineList] = useState([]);
  const [areaList, setAreaList] = useState(
    mode === FORM_MODE.CREATE ? avalancheAreas.filter((x) => !x.isDisabled) : avalancheAreas,
  );
  const avalanchePrograms = mode === FORM_MODE.CREATE ? programAreas.filter((x) => !x.isDisabled) : programAreas;

  useEffect(() => {
    setLoading(true);
    const avalancheProgram = avalanchePrograms.find((x) => x.value === area.parentId);
    if (avalancheProgram) {
      const filterCriteria =
        mode === FORM_MODE.CREATE
          ? (x) => x.parentId === avalancheProgram.value && !x.isDisabled
          : (x) => x.parentId === avalancheProgram.value;
      setAreaList(avalancheAreas.filter(filterCriteria).sort((a, b) => a.name.localeCompare(b.name)));
    }
    if (mode !== FORM_MODE.CREATE) {
      loadFullData();
    }
    if (mode === FORM_MODE.EDIT) {
      loadGeographicPoint();
    }
  }, [mode]);

  const loadFullData = async () => {
    const url = `${REST_API.NON_SPATIAL_REFERENCE_DATA}geographiclinebypoint/${data.value}`;

    GetJson(url)
      .then((response) => response.json())
      .then(async (records) => {
        if (records) {
          setLineList(records);
          setInitialDto(
            crudService.mapInitialValues({
              entity: roadClosureLocationData,
              area,
              avalanchePrograms,
            }),
          );
        }
        setLoading(false);
      });
  };

  const loadGeographicPoint = async () => {
    const url = `${REST_API.NON_SPATIAL_REFERENCE_DATA}geographicpoint/${data.value}`;

    GetJson(url)
      .then((response) => response.json())
      .then(async (record) => {
        if (record) {
          roadClosureLocationData.ConcurrencyControlNumber = record.ConcurrencyControlNumber;

          setInitialDto(
            crudService.mapInitialValues({
              entity: roadClosureLocationData,
              area,
              avalanchePrograms,
            }),
          );
        }
      });
  };

  const onEditButtonClick = async () => {
    setMode(FORM_MODE.EDIT);
  };

  const createRoadClosureLocations = (setSubmitting, resetForm, setErrors, roadClosureLocationDto) => {
    const url = `${REST_API.NON_SPATIAL_REFERENCE_DATA}geographicpoint`;

    PostJson(url, roadClosureLocationDto)
      .then(async (response) => {
        if (response.ok) {
          const roadClosureLocation = await response.json();
          const rcLocation = {
            value: roadClosureLocation.Id,
            label: `${roadClosureLocation.Name} - ${roadClosureLocation.Description}`,
            name: roadClosureLocation.Name,
            areaId: roadClosureLocation.AreaId,
            programId: avalanchePrograms.find((x) => x.value === roadClosureLocation.AreaId),
            description: roadClosureLocation.Description,
            isDisabled: !roadClosureLocation.IsActive,
            longitude: roadClosureLocation.Longitude,
            latitude: roadClosureLocation.Latitude,
          };
          updateData(rcLocation);
          setData(rcLocation);
          const newArea = avalancheAreas.find((x) => x.value === rcLocation.areaId);

          const updatedDto = crudService.mapInitialValues({
            entity: rcLocation,
            area: newArea,
            avalanchePrograms,
          });
          resetForm({
            values: updatedDto,
          });
          setInitialDto(updatedDto);
          toast.success(successMessage);
          setMode(FORM_MODE.VIEW);
        } else if (response.status === 412) {
          const errors = await response.json();
          setErrors(errors);
          resetForm({
            values: {
              program: avalanchePrograms.find((x) => x.value === area.parentId),
              area: area ?? avalancheAreas.find((x) => x.value === roadClosureLocationDto.AreaId),
              name: roadClosureLocationDto.Name,
              description: roadClosureLocationDto.Description,
              longitude: roadClosureLocationDto.Longitude,
              latitude: roadClosureLocationDto.Latitude,
              active: roadClosureLocationDto.IsActive,
            },
          });
          if (errors.entity) setEntityErrors({ ['Road Closure Location']: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const updateRoadClosureLocations = (setSubmitting, resetForm, setErrors, roadClosureLocationDto) => {
    const url = `${REST_API.NON_SPATIAL_REFERENCE_DATA}geographicpoint/${roadClosureLocationDto.value}`;
    PutJson(url, roadClosureLocationDto)
      .then(async (response) => {
        if (response.ok) {
          const roadClosureLocation = await response.json();
          updateData({});

          const entity = {
            name: roadClosureLocation.Name,
            description: roadClosureLocation.Description,
            longitude: roadClosureLocation.Longitude,
            latitude: roadClosureLocation.Latitude,
            isDisabled: !roadClosureLocation.IsActive,
          };

          const updatedDto = crudService.mapInitialValues({
            entity,
            area,
            avalanchePrograms,
          });
          resetForm({
            values: updatedDto,
          });
          setInitialDto(updatedDto);

          toast.success(successMessage);
          setMode(FORM_MODE.VIEW);
        } else if (response.status === 412) {
          const errors = await response.json();

          resetForm({
            values: {
              program: avalanchePrograms.find((x) => x.value === area.parentId),
              area: area,
              name: data.name,
              description: data.description,
              longitude: data.longitude,
              latitude: data.latitude,
              active: !data.isDisabled,
            },
          });
          setErrors(errors);
          if (errors.entity) setEntityErrors({ ['Road Closure Location']: errors.entity });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const updateData = (data) => {
    if (mode !== FORM_MODE.CREATE) {
      const index = areaPoints.findIndex((x) => x.value === roadClosureLocationData.value);
      if (index < 0) return;
      const newLocation = {
        value: roadClosureLocationData.value,
        label: `${roadClosureLocationData.name} - ${roadClosureLocationData.description}`,
        name: roadClosureLocationData.name,
        areaId: areaId ?? area.value,
        programId:
          roadClosureLocationData.programId ?? avalanchePrograms.find((x) => x.value === (areaId ?? area.value)),
        description: roadClosureLocationData.description,
        isDisabled: roadClosureLocationData.isDisabled,
        longitude: roadClosureLocationData.longitude,
        latitude: roadClosureLocationData.latitude,
      };
      areaPoints[index] = { ...newLocation };
      setAreaPoints([...areaPoints]);
    } else {
      roadClosureLocationData.value = data.value;
      setAreaPoints([
        ...areaPoints,
        {
          value: data.value,
          label: `${data.name} - ${data.description}`,
          name: data.name,
          areaId: data.areaId,
          programId: data.programId ?? avalanchePrograms.find((x) => x.value === data.areaId),
          description: data.description,
          isDisabled: data.isDisabled,
          longitude: data.longitude,
          latitude: data.latitude,
        },
      ]);
    }
  };

  const programOnChange = (opt, setFieldValue) => {
    setFieldValue('area', {}, false);
    area.value = null;
    const filterCriteria =
      mode === FORM_MODE.CREATE ? (x) => x.parentId === opt.value && !x.isDisabled : (x) => x.parentId === opt.value;
    setAreaList(avalancheAreas.filter(filterCriteria).sort((a, b) => a.name.localeCompare(b.name)));
  };

  const areaOnChange = (opt, setFieldValue) => {
    setAreaId(opt.value);
    var program = avalanchePrograms.find((x) => x.value === opt.parentId);
    setFieldValue('program', program);
    const filterCriteria =
      mode !== FORM_MODE.EDIT
        ? (x) => x.parentId === program.value && !x.isDisabled
        : (x) => x.parentId === program.value;
    setAreaList(avalancheAreas.filter(filterCriteria).sort((a, b) => a.name.localeCompare(b.name)));
  };

  const breadCrumbs = () => {
    setAreaList([]);
    setArea({});
    area.value = null;
    setMode(FORM_MODE.LIST);
  };

  const handleSubmit = (values, { setSubmitting, resetForm, setErrors }) => {
    setSubmitting(true);
    roadClosureLocationData.name = values.name;
    roadClosureLocationData.description = values.description;
    roadClosureLocationData.isDisabled = values.isDisabled;
    roadClosureLocationData.longitude = values.longitude;
    roadClosureLocationData.latitude = values.latitude;
    roadClosureLocationData.isDisabled = !values.active;
    const selectedAreaId = areaId ?? values?.area?.value;

    const roadClosureLocationDto = {
      ...roadClosureLocationData,
      AreaId: selectedAreaId,
      Name: values.name,
      Description: values.description,
      IsActive: values.active ?? true,
      Longitude: values.longitude,
      Latitude: values.latitude,
    };

    const newArea = avalancheAreas.find((x) => x.value === selectedAreaId);
    if (newArea != null) {
      setArea(newArea);
    }

    if (mode === FORM_MODE.EDIT) {
      updateRoadClosureLocations(setSubmitting, resetForm, setErrors, roadClosureLocationDto);
    } else {
      createRoadClosureLocations(setSubmitting, resetForm, setErrors, roadClosureLocationDto);
    }
  };

  const onClickCancel = () => {
    cancelOperation();
  };

  const cancelOperation = () => {
    setAreaList([]);
    setArea({});
    area.value = null;
    setMode(FORM_MODE.LIST);
  };

  const redirectToRoadClosureAreas = (locationMode, data) => {
    const locationData = areaLines.find((x) => x.value == data.Id);
    const locationArea = area;
    navigate('/RoadClosureAreas', {
      state: { locationMode, locationData, locationArea },
    });
  };

  const columns = [{ label: 'Name', key: 'Name' }];

  return (
    <>
      <Formik
        initialValues={crudService.mapInitialValues({
          entity: roadClosureLocationData,
          area,
          avalanchePrograms,
        })}
        validateOnMount={true}
        validationSchema={Yup.object({
          program: Yup.object()
            .nullable()
            .test('program', 'Program is a required field', (value, context) => {
              return (
                (!isValueEmpty(value) && !isValueEmpty(value.value)) ||
                (context && context.originalValue && context.originalValue.length > 0)
              );
            }),
          area: Yup.object()
            .nullable()
            .test('area', 'Area is a required field', (value, context) => {
              return (
                (!isValueEmpty(value) && !isValueEmpty(value.value)) ||
                (context && context.originalValue && context.originalValue.length > 0)
              );
            }),
          name: Yup.string()
            .nullable()
            .max(50, 'Maximum 50 characters are allowed')
            .required('Road Closure Location Name is a required field'),
          description: Yup.string()
            .nullable()
            .max(250, 'Maximum 250 characters are allowed')
            .required('Road Closure Location Description is a required field'),
          longitude: Yup.string()
            .nullable()
            .test('checkwslongitude', function () {
              const longitude = parseFloat(this.parent.longitude);
              if (
                this.parent.statustypeid &&
                (this.parent.statustypeid.label === 'Active' || this.parent.statustypeid.label === 'Test')
              ) {
                if (isValueEmpty(this.parent.longitude)) {
                  return this.createError({
                    message: 'Longitude is required',
                    path: 'longitude',
                  });
                }
              }
              if (!isValueEmpty(this.parent.longitude) && !isIntStr(this.parent.longitude)) {
                return this.createError({
                  message: 'Please enter a valid number',
                  path: 'longitude',
                });
              }
              if (this.parent.longitude < -180.0 || this.parent.longitude > 180.0) {
                return this.createError({
                  message: 'Please enter a valid number',
                  path: 'longitude',
                });
              }
              if (longitude < -139.06 || longitude > -114.03) {
                return this.createError({
                  message: 'Longitude must be within British Columbia (-139.06 to -114.03)',
                  path: 'longitude',
                });
              }
              return true;
            })
            .required('Longitude is a required field'),
          latitude: Yup.string()
            .nullable()
            .test('checkwslatitude', function () {
              const latitude = parseFloat(this.parent.latitude);
              if (
                this.parent.statustypeid &&
                (this.parent.statustypeid.label === 'Active' || this.parent.statustypeid.label === 'Test')
              ) {
                if (isValueEmpty(this.parent.latitude)) {
                  return this.createError({
                    message: 'Latitude is required',
                    path: 'latitude',
                  });
                }
              }
              if (!isValueEmpty(this.parent.latitude) && !isIntStr(this.parent.latitude)) {
                return this.createError({
                  message: 'Please enter a valid number',
                  path: 'latitude',
                });
              }
              if (latitude < -90.0 || latitude > 90.0) {
                return this.createError({
                  message: 'Please enter a valid number',
                  path: 'latitude',
                });
              }
              if (latitude < 48.3 || latitude > 60.0) {
                return this.createError({
                  message: 'Latitude must be within British Columbia (48.30 to 60.00)',
                  path: 'latitude',
                });
              }
              return true;
            })
            .required('Latitude is a required field'),
        })}
        onSubmit={handleSubmit}
      >
        {(props) => (
          <Form>
            <span className="awp-text-btn" onClick={breadCrumbs}>
              List Road Closure Locations
            </span>
            <span className="mx-3">&gt;</span>
            <span>{title}</span>
            <Card border="light" className="page-card">
              <Card.Header>
                <Row className="mt-4"></Row>
                {crudService.crud.getHeader()}
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col sm={6}>
                    <FormSelect
                      className="avalanche-select"
                      readOnly={readOnly || mode === FORM_MODE.EDIT}
                      name="program"
                      id="program"
                      label="Program"
                      useMargin={true}
                      postChange={(e) => {
                        programOnChange(e, props.setFieldValue);
                      }}
                      options={avalanchePrograms}
                      isMulti={false}
                    />
                  </Col>
                  <Col sm={6}>
                    <FormSelect
                      className="avalanche-select"
                      readOnly={readOnly || mode === FORM_MODE.EDIT}
                      name="area"
                      id="area"
                      label="Avalanche Area"
                      useMargin={true}
                      postChange={(e) => {
                        areaOnChange(e, props.setFieldValue);
                      }}
                      options={areaList}
                      isMulti={false}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={6} className="mb-4">
                    <FormInput
                      label="Road Closure Location Name"
                      instruction="(50 Characters max)"
                      readOnly={readOnly || roadClosureLocationData.IsProtected === 'Y'}
                      name="name"
                      id="name"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={8} className="mb-4">
                    <FormTextArea
                      readOnly={readOnly}
                      label="Road Closure Location Description"
                      instruction="(250 Characters max)"
                      id="description"
                      name="description"
                      rows={3}
                    />
                  </Col>
                </Row>
                <Row>
                  {loading && mode !== FORM_MODE.CREATE ? (
                    <Container>
                      <Spinner animation="border" />
                    </Container>
                  ) : (
                    <>
                      <Col sm={8}>
                        {lineList?.length > 0 ? (
                          <>
                            <FormListWithAction
                              label="Road Closure Areas"
                              colLabel=""
                              id="road-closure-area"
                              name="road-closure-area"
                              items={lineList}
                              readOnly={false}
                              columns={columns}
                              onActionClick={(item) => {
                                redirectToRoadClosureAreas(FORM_MODE.VIEW, item);
                              }}
                            />
                          </>
                        ) : mode !== FORM_MODE.CREATE ? (
                          <>
                            <div className="flex-grow-1">
                              <b>Road Closure Areas</b>
                            </div>
                            <div className="mt-4">
                              <p>There are no related areas</p>
                            </div>
                          </>
                        ) : (
                          <></>
                        )}
                      </Col>
                    </>
                  )}
                </Row>
                <Row>
                  <Col sm={6} className="mb-4">
                    <FormSpatialInput
                      label="Latitude"
                      instruction=""
                      readOnly={readOnly}
                      name="latitude"
                      id="latitude"
                    />
                  </Col>
                  <Col sm={6} className="mb-4">
                    <FormSpatialInput
                      label="Longitude"
                      instruction=""
                      readOnly={readOnly}
                      name="longitude"
                      id="longitude"
                    />
                  </Col>
                </Row>
                {mode !== FORM_MODE.CREATE ? (
                  <Row>
                    <Col sm={6} className="mb-4">
                      <FormCheckbox
                        useMargin={true}
                        readOnly={
                          readOnly ||
                          (!roadClosureLocationData.isDisabled &&
                            lineList?.length > 0 &&
                            lineList.some((x) => x.IsActive === 'Y'))
                        }
                        id="active"
                        name="active"
                        label="Active"
                        value={!roadClosureLocationData.isDisabled}
                      />
                    </Col>
                  </Row>
                ) : (
                  <></>
                )}
              </Card.Body>
              <Card.Footer>
                {!readOnly ? (
                  <AwpCloseCancelButtons
                    props={props}
                    isPristine={isEqual(props.values, initialDto)}
                    hasAccess={true}
                    onConfirmedCancel={() => {
                      onClickCancel();
                    }}
                    wrapperClass="mr-3"
                  />
                ) : (
                  <div className="mr-3">
                    <Button variant="outline-primary" onClick={() => onClickCancel()}>
                      {!props.dirty ? 'Close' : 'Cancel'}
                    </Button>
                    <Authorize requires={[Permissions.WriteAdminTools]}>
                      <Button className="ml-2" variant="outline-primary" onClick={onEditButtonClick}>
                        Edit Road Closure Location
                      </Button>
                    </Authorize>
                  </div>
                )}
              </Card.Footer>
            </Card>
          </Form>
        )}
      </Formik>
      <AwpValidationErrorModal setErrors={setEntityErrors} errors={entityErrors} />
    </>
  );
};

export default RoadClosureLocationsEdit;
